const fakeLocation = { pathname: window.location?.pathname || "/" };

import React, { useState, useEffect, useRef } from "react";
import { Message, UIState } from "./types/chat";
import { scrollToBottom } from "./utils/scrollToBottom";
import ChatHeader from "./components/ChatHeader";
import ChatMessages from "./components/ChatMessages";
import ChatInput from "./components/ChatInput";
import { MicButtonState } from "./components/MicButton";
import { useChatAPI } from "./hooks/useChatAPI";
import { useDynamicUI } from "./hooks/useDynamicUI";
import { useVoiceRecording } from "./hooks/useVoiceRecording";
import "./styles/typography.css";
import styles from "./styles/ChatClassification.module.css";

const ChatWithClassification: React.FC = () => {
  const location = fakeLocation;
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uiState, setUiState] = useState<UIState>({
    title: "Bem-vindo ao assistente virtual do FORINSIA!",
    subTitle: "Coloque aqui as suas questões ou dúvidas sobre a utilização do software.",
    examples: [],
    supportLink: "https://suporte.insia.pt/"
  });

  const { sendQuestion } = useChatAPI();
  useDynamicUI({ dynamicRoute: null, location, setUiState });

  const {
    recordingState,
    errorMessage: voiceErrorMessage,
    errorCount: voiceErrorCount,
    isSupported: voiceSupported,
    startRecording,
    stopRecording,
    cancelRecording,
    cancelTranscription,
    analyserRef,
  } = useVoiceRecording();

  const pushSystemNotice = (text: string) => {
    setMessages(prev => [
      ...prev,
      {
        id: "voice-notice-" + Date.now(),
        text,
        sender: "bot",
        isError: true,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  };

  useEffect(() => {
    // Show error notification even if the same error message occurs twice.
    // voiceErrorCount increments each time a new error is set, triggering this effect.
    if (voiceErrorMessage) pushSystemNotice(voiceErrorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceErrorMessage, voiceErrorCount]);

  // Mensagem inicial (boas-vindas)
  useEffect(() => {
    if (messages.length === 0) {
      const welcome: Message = {
        id: "welcome",
        text: `${uiState.title}\n\n${uiState.subTitle}\n\nConsulte o portal de suporte da INSIA [aqui](${uiState.supportLink}).`,
        sender: "bot",
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages([welcome]);
    }
  }, [uiState]);

  useEffect(() => {
    (document.querySelector("textarea") as HTMLTextAreaElement)?.focus();
    const scrollDelay = setTimeout(() => {
      scrollToBottom("." + styles.chatMessages);
    }, 100); // pequeno atraso para deixar o Framer Motion terminar
    return () => clearTimeout(scrollDelay);
  }, [messages]);


  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = async (overrideText?: string) => {
    if (isLoading) return;

    const textToSend = typeof overrideText === "string" ? overrideText : inputText;
    if (!textToSend.trim()) return;

    const userMsg: Message = {
      id: String(Date.now()),
      text: textToSend,
      sender: "user",
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const botResponse = await sendQuestion({ question: textToSend, messages, location, signal: abortController.signal });
      setMessages(prev => [...prev, botResponse]);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // User-initiated cancel — no error message, no response to append.
      } else {
        setMessages(prev => [
          ...prev,
          {
            id: "error-" + Date.now(),
            text: "Erro de comunicação. Verifique a ligação.",
            sender: "bot",
            isError: true
          }
        ]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const cancelGeneration = () => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
  };

  const handleMicClick = async () => {
    if (recordingState === "recording") {
      try {
        const { text, noSpeech } = await stopRecording();
        if (noSpeech) {
          pushSystemNotice("Não foi detetada fala. Tente novamente.");
        } else {
          setInputText(text);
        }
      } catch {
        // useVoiceRecording already set voiceErrorMessage; the effect above surfaces it.
      }
    } else {
      await startRecording();
    }
  };

  const micState: MicButtonState =
    recordingState === "recording"
      ? "recording"
      : recordingState === "transcribing"
      ? "transcribing"
      : isLoading
      ? "disabled"
      : "idle";

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Minimizar (para integração ASP.NET)
  const handleMinimize = () => {
    if (recordingState === "recording") {
      cancelRecording();
    }
    const container = document.getElementById("chatbot-widget-container");
    if (container) {
      container.style.opacity = "0";
      container.style.transform = "translateY(20px)";
      setTimeout(() => (container.style.display = "none"), 250);
    }
  };

  return (
    <div className={styles.chatContainer}>
      <ChatHeader onMinimize={handleMinimize} />
      <ChatMessages messages={messages} isLoading={isLoading} />
      <ChatInput
        inputText={inputText}
        setInputText={setInputText}
        sendMessage={sendMessage}
        handleKeyPress={handleKeyPress}
        isLoading={isLoading}
        micState={micState}
        micSupported={voiceSupported}
        onMicClick={handleMicClick}
        onCancelRecording={cancelRecording}
        onCancelTranscription={cancelTranscription}
        onCancel={cancelGeneration}
        analyserRef={analyserRef}
      />
    </div>
  );
};

export default ChatWithClassification;
